import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useParams, Link } from "react-router-dom";

import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
  Badge,
} from "reactstrap";
import {
  getTeamRecognitionMonthlyById,
  getTeamRecognitionMonthlyDetails,
} from "model/TeamRecognitionMonthlyModel";
import Loaders from "components/Common/Loaders";
import { urlEndPoint } from "helpers/GeneralHelper";

/** API may return { data: [...] } or nested shapes */
const normalizeDetailsRecords = (response) => {
  if (!response) return [];
  const top = response?.data !== undefined ? response.data : response;
  if (Array.isArray(top)) return top;
  if (Array.isArray(top?.data)) return top.data;
  if (Array.isArray(top?.records)) return top.records;
  return [];
};

const hasId = (v) => v != null && String(v).trim() !== "";

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** e.g. 28/Apr */
const formatDayShortMonth = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()}/${MONTH_SHORT[d.getMonth()]}`;
};

const normalizeMonthlyHeader = (response) => {
  if (!response) return null;
  const raw = response?.data !== undefined ? response.data : response;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  return null;
};

const PointValue = ({ value }) => {
  const n = Number(value);
  if (Number.isNaN(n)) return <span className="text-muted">—</span>;
  if (n > 0) {
    return <span className="tr-point tr-point--pos">+{n}</span>;
  }
  if (n < 0) {
    return <span className="tr-point tr-point--neg">{n}</span>;
  }
  return <span className="tr-point tr-point--zero">0</span>;
};

const GroupBlock = ({ item, isTeamContext }) => {
  const displayTitle = isTeamContext
    ? item?.teamName || item?.name || "Team"
    : item?.name || item?.clientName || "—";
  const subtitle = item?.username ? (
    <div className="tr-detail-username text-muted small">{item.username}</div>
  ) : null;
  const points = Array.isArray(item?.points) ? item.points : [];
  const total = item?.teamRecognitionPoint;

  return (
    <Card className="tr-detail-group shadow-sm border-0 mt-2">
      <div className="tr-detail-group-head px-3 py-2">
        <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2">
          <div>
            <span className="tr-detail-name fw-bold">{displayTitle}</span>
            {/* {subtitle} */}
          </div>
        </div>
      </div>
      <div className="px-0 pb-2">
        <div className="table-responsive">
          <Table className="tr-detail-table mb-0" borderless>
            <thead>
              <tr>
                <th className="tr-sn">S.N.</th>
                <th>Notes</th>
                <th className="tr-point-col text-end">Point</th>
              </tr>
            </thead>
            <tbody>
              {points.length ? (
                points.map((p, idx) => (
                  <tr key={p?.id || `${p?.pointId}-${idx}`}>
                    <td className="tr-sn text-muted">{p?.pointId ?? idx + 1}</td>
                    <td className="tr-note">{p?.note || "—"}</td>
                    <td className="text-end">
                      <PointValue value={p?.point} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-muted text-center py-3">
                    No point entries
                  </td>
                </tr>
              )}
              <tr className="tr-total-row">
                <td colSpan={2} className="fw-semibold text-end pe-2">
                  Total
                </td>
                <td className="text-end tr-total-cell">
                  {total != null && total !== "" ? (
                    <span>
                      <PointValue value={total} />
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  );
};

const TeamRecognitionMonthlyDetails = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [monthHeader, setMonthHeader] = useState(null);
  const [activeTab, setActiveTab] = useState("client");

  const loadDetails = useCallback(async () => {
    if (!id) {
      setRecords([]);
      setMonthHeader(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [headerRes, detailsRes] = await Promise.all([
        getTeamRecognitionMonthlyById(id),
        getTeamRecognitionMonthlyDetails(id),
      ]);
      setMonthHeader(normalizeMonthlyHeader(headerRes));
      setRecords(normalizeDetailsRecords(detailsRes));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const { clientTabData, teamItems } = useMemo(() => {
    const list = Array.isArray(records) ? records : [];
    const withClient = list.filter((r) => hasId(r?.clientId));
    const withTeam = list.filter((r) => hasId(r?.teamId));
    const neither = list.filter(
      (r) => !hasId(r?.clientId) && !hasId(r?.teamId)
    );
    // Rows without teamId/clientId (e.g. user-level only) show under Client
    return { clientTabData: [...withClient, ...neither], teamItems: withTeam };
  }, [records]);

  useEffect(() => {
    if (clientTabData.length && !teamItems.length) setActiveTab("client");
    else if (teamItems.length && !clientTabData.length) setActiveTab("team");
  }, [clientTabData.length, teamItems.length]);

  const showClientTab = clientTabData.length > 0;
  const showTeamTab = teamItems.length > 0;
  const activeList = activeTab === "client" ? clientTabData : teamItems;
  const emptyBoth = !loading && !clientTabData.length && !teamItems.length;

  const monthTitleLine = useMemo(() => {
    if (!monthHeader) return null;
    const parts = [monthHeader.monthLabel, monthHeader.yearLabel].filter(Boolean);
    return parts.length ? parts.join(" ") : null;
  }, [monthHeader]);

  /** e.g. (28/Apr To 25/May) — same style as "April (30/Mar To 26/Apr)" */
  const monthRangeLine = useMemo(() => {
    if (!monthHeader) return null;
    const from = formatDayShortMonth(monthHeader.teamRecognitionMonthFrom);
    const to = formatDayShortMonth(monthHeader.teamRecognitionMonthTo);
    if (!from || !to) return null;
    return `(${from} To ${to})`;
  }, [monthHeader]);

  const showMonthBanner = Boolean(monthTitleLine || monthRangeLine);

  return (
    <div className="page-content team-recognition-details-page">
      <Container fluid>
        <Card className="mb-2 border-0 shadow-sm tr-details-content-card">
          <CardBody className="p-0">
            {loading ? (
              <div className="p-4">
                <Loaders />
              </div>
            ) : (
              <>
                {showMonthBanner && (
                  <div className="tr-month-range-banner">
                    <Col xs="auto" className="text-end">
                <Link
                  to={`/${urlEndPoint.teamRecognitionMonthlyList}`}
                  className="btn btn-light border btn-sm"
                >
                  <i className="bx bx-chevron-left me-1" />
                  Back to monthly list
                </Link>
              </Col>
                    {monthTitleLine && (
                      <div className="tr-month-range-title text-center">{monthTitleLine}</div>
                    )}
                    {monthRangeLine && (
                      <div className="tr-month-range-dates text-center">{monthRangeLine}</div>
                    )}
                  </div>
                )}

                {emptyBoth ? (
                  <div className="tr-empty-state p-5 text-center text-muted">
                    <i className="bx bx-line-chart display-4 d-block mb-2 opacity-25" aria-hidden />
                    <span className="fw-semibold">No recognition data for this month.</span>
                  </div>
                ) : (
                  <>
                    {(showClientTab || showTeamTab) && (
                      <div className="tr-details-nav px-3 pt-2">
                        <Nav className="tr-details-tabs" pills>
                          {showClientTab && (
                            <NavItem>
                              <NavLink
                                href="#"
                                className={activeTab === "client" ? "active" : ""}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActiveTab("client");
                                }}
                              >
                                <i className="bx bx-user me-1" />
                                Users
                                <Badge
                                  className="ms-2 tr-tab-count bg-light text-body border"
                                  pill
                                >
                                  {clientTabData.length}
                                </Badge>
                              </NavLink>
                            </NavItem>
                          )}
                          {showTeamTab && (
                            <NavItem>
                              <NavLink
                                href="#"
                                className={activeTab === "team" ? "active" : ""}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActiveTab("team");
                                }}
                              >
                                <i className="bx bx-group me-1" />
                                Team
                                <Badge
                                  className="ms-2 tr-tab-count bg-light text-body border"
                                  pill
                                >
                                  {teamItems.length}
                                </Badge>
                              </NavLink>
                            </NavItem>
                          )}
                        </Nav>
                      </div>
                    )}

                    <div className="p-3 pt-0">
                      {activeList.length === 0 ? (
                        <div className="tr-empty-state tr-empty-state--tab p-5 text-center text-muted">
                          <i className="bx bx-line-chart display-4 d-block mb-2 opacity-25" aria-hidden />
                          <span className="fw-semibold">No data found</span>
                        </div>
                      ) : (
                        activeList.map((item) => (
                          <GroupBlock
                            key={item?.id || item?.userId || item?.teamId}
                            item={item}
                            isTeamContext={activeTab === "team"}
                          />
                        ))
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

TeamRecognitionMonthlyDetails.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(TeamRecognitionMonthlyDetails);
